/**
 * Home Value review lane (BOT-411) — the availability counterpart of `checks.ts`.
 *
 * A proposal answers one question: what should the storefront promise once BRW's
 * own stock is sold out? It is computed from the vendor's per-PDC stock table and
 * compared with the Home Value NetSuite carries today; approving it PATCHes
 * exactly one NetSuite attribute.
 */

import { reviewRequestError } from '../utils/reviewErrors';

/** The three NetSuite Home Value labels, in ascending lead time. */
export type HomeValue = 'Ships in 1-3 days' | 'Ships in 7-10 days' | 'Extended Lead Time';

export const HOME_VALUES: HomeValue[] = [
  'Ships in 1-3 days',
  'Ships in 7-10 days',
  'Extended Lead Time',
];

/** Filter value for "NetSuite has no Home Value at all" — a real segment. */
export const NO_CURRENT_VALUE = '__none__';

export type HomeValueReviewStatus =
  | 'pending'
  | 'approved'
  | 'auto_approved'
  | 'rejected'
  | 'cancelled';

export type HomeValueReviewAction = 'approved' | 'rejected';

/** Which fact produced the proposal. Mirrors `availability/rules_config.py`. */
export type HomeValueReasonCode =
  | 'brand_7_10'
  | 'brand_extended'
  | 'pdc_primary'
  | 'pdc_secondary'
  | 'pdc_extended_only'
  | 'pdc_zero'
  | 'pdc_unknown_location'
  | 'total_binary'
  | 'nof_direct_ship'
  | 'dealer_stock_only'
  | 'no_pdc_section'
  | 'dealer_home_stock'
  | 'dealer_network_stock'
  | 'dealer_stock_thin'
  | 'pdc_status'
  | 'source_with_lead_time'
  | 'special_order'
  | 'no_data'
  | 'stale'
  | 'ns_already_matches';

/**
 * The three `pdc_*` tiers are the vendor's lead-time map (`PdcLeadTimeMap`):
 * primary = 1-3 days, secondary = 7-10 days, extended = never a promise. For
 * Paccar (client matrix 2026-09-04) primary is every US PDC, secondary is
 * Toronto alone, and the extended tier is empty.
 */
export const REASON_LABELS: Record<HomeValueReasonCode, string> = {
  brand_7_10:           'Brand policy (7-10 days)',
  brand_extended:       'Brand policy (extended)',
  pdc_primary:          '1-3 day PDC in stock',
  pdc_secondary:        '7-10 day PDC only',
  pdc_extended_only:    'Extended-tier PDC only',
  pdc_zero:             'All PDCs empty',
  pdc_unknown_location: 'Unclassified location',
  total_binary:         'Vendor stock total',
  // Paccar: the portal printed <NOF> ("not on file") — a direct-ship part, the
  // vendor's own lead time applies (client, 2026-09-08).
  nof_direct_ship:      'Not on file — direct ship',
  // Paccar: every PDC cell blank but the dealer column carries a number, which
  // proves the page rendered (client, 2026-09-05).
  dealer_stock_only:    'PDC blank, dealer stock proves render',
  // Freightliner: the modal has dealer rows only, no PDC section at all.
  no_pdc_section:       'No PDC section (dealer rows only)',
  // Freightliner dealer fallback (client, 2026-09-15) — our own branches, read
  // only when no PDC holds stock.
  dealer_home_stock:    'Home dealer ≥ 3 (no PDC stock)',
  dealer_network_stock: 'Dealer network ≥ 3 (no PDC stock)',
  dealer_stock_thin:    'Dealer network < 3 (no PDC stock)',
  // International: the portal reports the PDC network as a status, not a
  // quantity — In Stock → 7-10 days, Out of Stock → Extended (BOT-407 rule).
  pdc_status:           'PDC status (In Stock / Out of Stock)',
  // CAT (client, 2026-09-03 / 09-14): any line of the qty=100 probe with stock
  // backs 1-3 days, whatever its lead time; nothing but Contact Dealer and / or
  // a bare CAT line is a special order from the factory.
  source_with_lead_time: 'Stock at a CAT source',
  special_order:        'Special order (Contact Dealer / CAT only)',
  no_data:              'No usable stock data',
  stale:                'Observation too old',
  ns_already_matches:   'NetSuite already matched',
};

/** Reason codes a *queued* proposal can carry (no_data / stale never queue). */
export const QUEUE_REASON_CODES: HomeValueReasonCode[] = [
  'pdc_primary',
  'pdc_secondary',
  'pdc_extended_only',
  'pdc_zero',
  'pdc_unknown_location',
  'total_binary',
  'nof_direct_ship',
  'dealer_stock_only',
  'no_pdc_section',
  'dealer_home_stock',
  'dealer_network_stock',
  'dealer_stock_thin',
  'pdc_status',
  'source_with_lead_time',
  'special_order',
  'brand_7_10',
  'brand_extended',
];

/** Everything Processed can show: a queued reason, or a recompute's retraction. */
export const PROCESSED_REASON_CODES: HomeValueReasonCode[] = [
  ...QUEUE_REASON_CODES,
  'ns_already_matches',
];

/**
 * Stock filters (client feedback on staging, 2026-09-15). They read the proposal
 * SNAPSHOT's totals — the numbers the operator sees in the row — never the live
 * availability, which can change under them.
 */
export type VendorStockFilter = 'in_stock' | 'zero' | 'no_data';
export type DealerStockFilter = 'in_stock' | 'none';

export const VENDOR_STOCK_OPTIONS: { value: VendorStockFilter | ''; label: string }[] = [
  { value: '',         label: 'All' },
  { value: 'in_stock', label: 'In stock (> 0)' },
  { value: 'zero',     label: 'Zero (portal confirmed 0)' },
  { value: 'no_data',  label: 'No readable number' },
];

export const DEALER_STOCK_OPTIONS: { value: DealerStockFilter | ''; label: string }[] = [
  { value: '',         label: 'All' },
  { value: 'in_stock', label: 'Dealer > 0' },
  { value: 'none',     label: 'No dealer stock' },
];

/** Verdicts that produced no value — the only reasons the No data page lists. */
export const SKIP_REASON_CODES: HomeValueReasonCode[] = ['no_data', 'stale'];

/**
 * Why the rule could not decide, derived server-side from the stored stock
 * table (`vendor_availability_repo.derive_shape`). A new column in effect —
 * the BOT-421 analysis only ever expressed this in free-text notes.
 */
export type NoDataShape =
  | 'no_stock_table'
  | 'readable'
  | 'pdc_blank'
  | 'pdc_unreadable'
  | 'pdc_not_on_file'
  | 'dealer_only'
  | 'dealer_only_unreadable';

export const SHAPE_LABELS: Record<NoDataShape, string> = {
  no_stock_table:         'No stock table in the payload',
  readable:               'Numbers parsed — skipped for age',
  pdc_blank:              'PDC cells blank',
  pdc_unreadable:         'PDC cells unreadable',
  pdc_not_on_file:        'Portal says not on file (vendor not configured)',
  dealer_only:            'Dealer rows only',
  dealer_only_unreadable: 'Dealer rows only, no readable quantity',
};

export const SHAPE_HINTS: Record<NoDataShape, string> = {
  no_stock_table:         'The scrape carried no location rows at all — a failed render, a hidden availability link (superseded part) or a part with no stock table. The re-check candidates.',
  readable:               'The stock table parsed, but the observation is older than the vendor TTL, so nothing was written.',
  pdc_blank:              'PDC rows are present and every cell is empty — a failed render and a total backorder look identical, so the client reviews these by hand.',
  pdc_unreadable:         'A PDC cell holds text the parser could not read — look at the screenshot.',
  pdc_not_on_file:        'The portal printed its not-on-file marker but this vendor has no ruling for it yet.',
  dealer_only:            'Dealer rows parsed but no PDC row exists and the vendor has no ruling for that shape.',
  dealer_only_unreadable: 'Only dealer rows, and none carries a number the portal printed — nothing proves the modal rendered.',
};

/** One row of a vendor's stock table, normalised across vendors. */
export interface AvailabilityLocation {
  code: string;
  /** null when the vendor printed something unreadable — not zero. */
  qty: number | null;
  kind: 'pdc' | 'dealer' | 'supplier' | 'unknown' | 'excluded';
  raw: string;
  flags: Record<string, unknown>;
}

export interface HomeValueCheck {
  id: number;
  vendor_code: string;
  vendor_name: string;
  ns_item_id: number;
  sku: string;
  /** tbnsdata.home_value when the proposal was made; null when NS had none. */
  current_home_value: string | null;
  proposed_home_value: string;
  reason_code: HomeValueReasonCode | null;
  /** Which PDC / brand / freshness fact produced the value (audit + tooltip). */
  rule_inputs: Record<string, unknown>;
  /** The stock table as seen at decision time — the source of the PDC chips. */
  locations: AvailabilityLocation[];
  pdc_total: number | null;
  dealer_total: number | null;
  availability_observed_at: string | null;
  /** Age of that observation in days, computed server-side. */
  age_days: number | null;
  review_status: HomeValueReviewStatus;
  reviewed_at: string | null;
  reviewer_email: string | null;
  ns_pushed_at: string | null;
  ns_push_error: string | null;
  created_at: string | null;
  screenshot_url: string | null;
  manufacturer: string | null;
  item_name: string | null;
  /** NetSuite stock — display only; it never enters the rule. */
  ns_on_hand: number | null;
  ns_available: number | null;
  /** tbnsdata.home_value as of the last ns_sync tick (post-push confirmation). */
  ns_home_value_now: string | null;
  magento_id: number | null;
  /** Live availability for the same vendor+sku — newer than the snapshot when a
   *  re-scrape has landed since the proposal was made. */
  live_observed_at: string | null;
  live_pdc_total: number | null;
  live_dealer_total: number | null;
  live_has_data: boolean | null;
}

export interface HomeValueChecksPage {
  items: HomeValueCheck[];
  total: number;
  page: number;
  per_page: number;
}

export interface VendorOption {
  code: string;
  name: string;
}

export interface HomeValueFilters {
  vendor_codes?: string[];
  review_status?: HomeValueReviewStatus;
  proposed_home_value?: string;
  /** A HomeValue label, or NO_CURRENT_VALUE for items with none. */
  current_home_value?: string;
  reason_codes?: HomeValueReasonCode[];
  manufacturer?: string;
  /** Search SKU or item name. */
  q?: string;
  vendor_stock?: VendorStockFilter;
  dealer_stock?: DealerStockFilter;
  observed_from?: string;
  observed_to?: string;
  page?: number;
  per_page?: number;
}

/** Queue lanes: who decided the row — an operator (`approved`) or the auto-approve
 *  gate (`auto_approved`). The API maps it to `review_status`. */
export type HomeValueQueueOrigin = 'manual' | 'auto';

export interface HomeValueQueueFilters {
  vendor_codes?: string[];
  origin?: HomeValueQueueOrigin;
  proposed_home_value?: string;
  current_home_value?: string;
  reason_codes?: HomeValueReasonCode[];
  manufacturer?: string;
  q?: string;
  vendor_stock?: VendorStockFilter;
  dealer_stock?: DealerStockFilter;
  queued_from?: string;
  queued_to?: string;
  page?: number;
  per_page?: number;
}

export interface HomeValueProcessedFilters {
  vendor_codes?: string[];
  review_status?: Exclude<HomeValueReviewStatus, 'pending'>;
  proposed_home_value?: string;
  current_home_value?: string;
  reason_codes?: HomeValueReasonCode[];
  manufacturer?: string;
  q?: string;
  vendor_stock?: VendorStockFilter;
  dealer_stock?: DealerStockFilter;
  synced_from?: string;
  synced_to?: string;
  page?: number;
  per_page?: number;
}

export interface HomeValueStats {
  by_review_status: Record<string, number>;
  by_reason_code: Record<string, number>;
  transitions: Array<{ current: string; proposed: string; count: number }>;
  total: number;
}

export interface ItemAvailability {
  vendor_code: string;
  vendor_name: string;
  sku: string;
  ns_item_id: number | null;
  observed_at: string | null;
  source: string | null;
  pdc_total: number | null;
  dealer_total: number | null;
  has_data: boolean;
  locations: AvailabilityLocation[];
  recorded_at: string | null;
  updated_at: string | null;
  /** The rule engine's latest verdict on the live row (absent on history rows). */
  rule_reason_code?: HomeValueReasonCode | null;
  rule_value?: string | null;
  rule_decided_at?: string | null;
  rule_inputs?: Record<string, unknown>;
  screenshot_url?: string | null;
}

/** One undecided item — a live availability row whose latest verdict wrote nothing. */
export interface HomeValueNoDataRow {
  id: number | null;
  vendor_code: string;
  vendor_name: string;
  sku: string;
  ns_item_id: number | null;
  observed_at: string | null;
  age_days: number | null;
  updated_at: string | null;
  pdc_total: number | null;
  dealer_total: number | null;
  has_data: boolean;
  locations: AvailabilityLocation[];
  rule_reason_code: HomeValueReasonCode | null;
  rule_value: string | null;
  /** When the verdict was stamped — the row keeps its last verdict even after
   *  the item drops out of the recompute's source query. */
  rule_decided_at: string | null;
  rule_inputs: Record<string, unknown>;
  warnings: string[];
  shape: NoDataShape;
  screenshot_url: string | null;
  manufacturer: string | null;
  item_name: string | null;
  /** The promise the storefront makes today — what makes an undecided row urgent. */
  ns_home_value_now: string | null;
  ns_on_hand: number | null;
  ns_available: number | null;
  magento_id: number | null;
}

export interface HomeValueNoDataPage {
  items: HomeValueNoDataRow[];
  total: number;
  page: number;
  per_page: number;
}

export interface HomeValueNoDataFilters {
  vendor_codes?: string[];
  /** Defaults to ['no_data'] server-side; 'stale' must be asked for. */
  reason_codes?: HomeValueReasonCode[];
  current_home_value?: string;
  manufacturer?: string;
  q?: string;
  observed_from?: string;
  observed_to?: string;
  page?: number;
  per_page?: number;
}

export interface HomeValueNoDataStats {
  by_reason: Record<string, number>;
  /** no_data rows by the storefront's current (normalised) promise; '(none)' for an empty one. */
  by_current_value: Record<string, number>;
  last_decided_at: string | null;
  total: number;
}

export interface ItemAvailabilityPanel {
  availability: ItemAvailability[];
  history: ItemAvailability[];
  checks: HomeValueCheck[];
}

const BASE = '/api/v1/home-value-checks';

/** The filter fields every Home Value list shares. */
interface CommonFilterFields {
  vendor_codes?: string[];
  review_status?: string;
  proposed_home_value?: string;
  current_home_value?: string;
  reason_codes?: HomeValueReasonCode[];
  manufacturer?: string;
  q?: string;
  vendor_stock?: VendorStockFilter;
  dealer_stock?: DealerStockFilter;
  page?: number;
  per_page?: number;
}

/** Shared query-string builder — the filter sets overlap almost entirely. */
function commonParams(f: CommonFilterFields): URLSearchParams {
  const params = new URLSearchParams();
  if (f.vendor_codes?.length) {
    f.vendor_codes.forEach(vc => params.append('vendor_code', vc));
  }
  if (f.review_status)       params.set('review_status', f.review_status);
  if (f.proposed_home_value) params.set('proposed_home_value', f.proposed_home_value);
  if (f.current_home_value)  params.set('current_home_value', f.current_home_value);
  if (f.reason_codes?.length) {
    f.reason_codes.forEach(rc => params.append('reason_code', rc));
  }
  if (f.manufacturer) params.set('manufacturer', f.manufacturer);
  if (f.q)            params.set('q', f.q);
  if (f.vendor_stock) params.set('vendor_stock', f.vendor_stock);
  if (f.dealer_stock) params.set('dealer_stock', f.dealer_stock);
  if (f.page)         params.set('page', String(f.page));
  if (f.per_page)     params.set('per_page', String(f.per_page));
  return params;
}

export async function fetchHomeValueChecks(
  filters: HomeValueFilters = {},
): Promise<HomeValueChecksPage> {
  const params = commonParams(filters);
  if (filters.observed_from) params.set('observed_from', filters.observed_from);
  if (filters.observed_to)   params.set('observed_to', filters.observed_to);

  const res = await fetch(`${BASE}?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Home Value checks fetch failed: ${res.status}`);
  return res.json() as Promise<HomeValueChecksPage>;
}

/** Home Value → Queue: decided (manually or automatically) and not yet pushed. */
export async function fetchHomeValueChecksQueue(
  filters: HomeValueQueueFilters = {},
): Promise<HomeValueChecksPage> {
  const params = commonParams(filters);
  if (filters.origin)      params.set('origin', filters.origin);
  if (filters.queued_from) params.set('queued_from', filters.queued_from);
  if (filters.queued_to)   params.set('queued_to', filters.queued_to);

  const res = await fetch(`${BASE}/queue?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Home Value queue fetch failed: ${res.status}`);
  return res.json() as Promise<HomeValueChecksPage>;
}

export async function fetchHomeValueChecksProcessed(
  filters: HomeValueProcessedFilters = {},
): Promise<HomeValueChecksPage> {
  const params = commonParams(filters);
  if (filters.synced_from) params.set('synced_from', filters.synced_from);
  if (filters.synced_to)   params.set('synced_to', filters.synced_to);

  const res = await fetch(`${BASE}/processed?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Home Value processed fetch failed: ${res.status}`);
  return res.json() as Promise<HomeValueChecksPage>;
}

export async function fetchHomeValueVendors(): Promise<VendorOption[]> {
  const res = await fetch(`${BASE}/vendor-codes`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Vendors fetch failed: ${res.status}`);
  const data = await res.json() as { vendors: VendorOption[] };
  return data.vendors;
}

export async function fetchHomeValueManufacturers(): Promise<string[]> {
  const res = await fetch(`${BASE}/manufacturers`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Manufacturers fetch failed: ${res.status}`);
  const data = await res.json() as { manufacturers: string[] };
  return data.manufacturers;
}

export async function fetchHomeValueStats(
  vendorCodes: string[] = [],
): Promise<HomeValueStats> {
  const params = new URLSearchParams();
  vendorCodes.forEach(vc => params.append('vendor_code', vc));
  const res = await fetch(`${BASE}/stats?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Home Value stats fetch failed: ${res.status}`);
  return res.json() as Promise<HomeValueStats>;
}

export async function fetchHomeValueNoData(
  filters: HomeValueNoDataFilters = {},
): Promise<HomeValueNoDataPage> {
  const params = commonParams(filters);
  if (filters.observed_from) params.set('observed_from', filters.observed_from);
  if (filters.observed_to)   params.set('observed_to', filters.observed_to);

  const res = await fetch(`${BASE}/no-data?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Home Value no-data fetch failed: ${res.status}`);
  return res.json() as Promise<HomeValueNoDataPage>;
}

export async function fetchHomeValueNoDataVendors(): Promise<VendorOption[]> {
  const res = await fetch(`${BASE}/no-data/vendor-codes`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Vendors fetch failed: ${res.status}`);
  const data = await res.json() as { vendors: VendorOption[] };
  return data.vendors;
}

export async function fetchHomeValueNoDataManufacturers(): Promise<string[]> {
  const res = await fetch(`${BASE}/no-data/manufacturers`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Manufacturers fetch failed: ${res.status}`);
  const data = await res.json() as { manufacturers: string[] };
  return data.manufacturers;
}

export async function fetchHomeValueNoDataStats(
  vendorCodes: string[] = [],
): Promise<HomeValueNoDataStats> {
  const params = new URLSearchParams();
  vendorCodes.forEach(vc => params.append('vendor_code', vc));
  const res = await fetch(`${BASE}/no-data/stats?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Home Value no-data stats fetch failed: ${res.status}`);
  return res.json() as Promise<HomeValueNoDataStats>;
}

export async function fetchItemAvailability(
  nsItemId: string | number,
): Promise<ItemAvailabilityPanel> {
  const res = await fetch(`${BASE}/item/${nsItemId}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Availability fetch failed: ${res.status}`);
  return res.json() as Promise<ItemAvailabilityPanel>;
}

export async function reviewHomeValueCheck(
  id: number,
  action: HomeValueReviewAction,
): Promise<void> {
  const res = await fetch(`${BASE}/${id}/review`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action }),
  });
  if (!res.ok) throw await reviewRequestError(res, 'Review');
}

export async function bulkReviewHomeValue(
  ids: number[],
  action: HomeValueReviewAction,
): Promise<number> {
  const res = await fetch(`${BASE}/bulk-review`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, action }),
  });
  if (!res.ok) throw await reviewRequestError(res, 'Bulk review');
  const data = await res.json() as { updated: number };
  return data.updated;
}

export async function undoHomeValueCheck(id: number): Promise<void> {
  const res = await fetch(`${BASE}/${id}/undo`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Undo failed: ${res.status}`);
}

/** True when a newer observation has landed since this proposal was computed —
 *  the operator is looking at a decision made on superseded numbers. */
export function isSuperseded(check: HomeValueCheck): boolean {
  if (!check.live_observed_at || !check.availability_observed_at) return false;
  return new Date(check.live_observed_at) > new Date(check.availability_observed_at);
}

/** Lead-time ordering, so the UI can tell an upgrade from a downgrade. */
const LEAD_TIME_RANK: Record<string, number> = {
  'ships in 1-3 days': 0,
  'ships in 7-10 days': 1,
  'extended lead time': 2,
};

function rank(value: string | null): number | undefined {
  if (!value) return undefined;
  // Tolerate Magento's "2. " option prefix and casing drift, exactly as the
  // backend's normalise_home_value does.
  const cleaned = value.trim().replace(/^\d+\.\s*/, '').toLowerCase();
  return LEAD_TIME_RANK[cleaned];
}

/**
 * Which way the promise moves: "slower" means we are about to tell customers to
 * wait longer (a lost-sale risk), "faster" means we are about to promise sooner
 * (a cancellation risk if wrong). That asymmetry is why the direction is shown
 * rather than just the two values.
 */
export function transitionDirection(
  check: Pick<HomeValueCheck, 'current_home_value' | 'proposed_home_value'>,
): 'faster' | 'slower' | 'same' | 'unknown' {
  const from = rank(check.current_home_value);
  const to = rank(check.proposed_home_value);
  if (from === undefined || to === undefined) return 'unknown';
  if (to < from) return 'faster';
  if (to > from) return 'slower';
  return 'same';
}
